import { useSyncExternalStore } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconCoin, IconKey } from '@tabler/icons-react';
import { formatKeysByDate, getKeysByDate } from 'components/marketplace/helpers/certaintyPanel';
import { formatCurrencyDollars } from 'helpers/formatters';

export type BuyBoxCertaintyStats = {
  // Principal and interest on the Buy Direct price, not the list price.
  monthlyPaymentEstimate: number | null;
  showKeysBy: boolean;
};

// The date only changes across days, never within a session, so there is
// nothing for useSyncExternalStore to subscribe to.
const subscribeToNothing = () => () => {};

const Stat = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <Row className="items-center gap-1.5">
    <Box className="shrink-0 text-od-content-secondary">{icon}</Box>
    <Text as="span" className="whitespace-nowrap text-body-sm text-od-content-secondary">
      {label}
    </Text>
  </Row>
);

export const BuyBoxStats = ({ stats }: { stats: BuyBoxCertaintyStats }) => {
  // Client-only: the SSR output is Cloudflare-cached by URL, so a
  // server-rendered "now"-based date would go stale and mismatch on hydration.
  // The server snapshot is null and the client's resolves after hydration.
  const keysByDate = useSyncExternalStore(
    subscribeToNothing,
    () => formatKeysByDate(getKeysByDate()),
    () => null,
  );

  const items = [
    stats.monthlyPaymentEstimate != null && (
      <Stat
        key="monthly"
        icon={<IconCoin size={18} />}
        label={`Est. ${formatCurrencyDollars(stats.monthlyPaymentEstimate)}/mo`}
      />
    ),
    stats.showKeysBy && keysByDate != null && (
      <Stat key="keys-by" icon={<IconKey size={18} />} label={`Keys by ${keysByDate}`} />
    ),
  ].filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return (
    <Row className="flex-wrap items-center gap-x-3 gap-y-2">
      {items.map((item, index) => (
        <Row key={index} className="items-center gap-x-3">
          {index > 0 && <Box className="h-4 w-px bg-od-border-primary" />}
          {item}
        </Row>
      ))}
    </Row>
  );
};
